/**
 * Chat Session Hooks
 *
 * TanStack Query hooks for chat session management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listChatSessions,
  getChatSession,
  deleteChatSession,
  createChatSession,
  updateChatSession,
  type ChatSession,
  type ChatMessage,
} from '@/lib/api-client'

// Query keys
export const chatSessionKeys = {
  all: ['chat-sessions'] as const,
  lists: () => [...chatSessionKeys.all, 'list'] as const,
  list: (filters?: { limit?: number; offset?: number; userId?: string }) =>
    [...chatSessionKeys.lists(), filters] as const,
  details: () => [...chatSessionKeys.all, 'detail'] as const,
  detail: (id: number) => [...chatSessionKeys.details(), id] as const,
}

/**
 * Hook to list chat sessions for a specific user
 */
export function useChatSessions(params?: { limit?: number; offset?: number; userId?: string }) {
  return useQuery({
    queryKey: chatSessionKeys.list(params),
    queryFn: () => listChatSessions(params),
    staleTime: 30 * 1000, // 30 seconds
    enabled: !!params?.userId, // Only fetch when userId is provided
  })
}

/**
 * Hook to get a specific chat session with messages
 */
export function useChatSession(sessionId: number | null) {
  return useQuery({
    queryKey: chatSessionKeys.detail(sessionId || 0),
    queryFn: () => getChatSession(sessionId!),
    enabled: sessionId !== null && sessionId > 0,
    staleTime: 60 * 1000, // 1 minute
  })
}

/**
 * Hook to delete a chat session
 */
export function useDeleteChatSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (sessionId: number) => deleteChatSession(sessionId),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: chatSessionKeys.detail(deletedId) })
      // Invalidate list
      queryClient.invalidateQueries({ queryKey: chatSessionKeys.lists() })
    },
  })
}

/**
 * Hook to create a new chat session
 */
export function useCreateChatSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params?: { title?: string; branchId?: number; modelId?: string }) =>
      createChatSession(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatSessionKeys.lists() })
    },
  })
}

/**
 * Hook to update a chat session (e.g., rename)
 */
export function useUpdateChatSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ sessionId, title }: { sessionId: number; title: string }) =>
      updateChatSession(sessionId, { title }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: chatSessionKeys.detail(variables.sessionId) })
      queryClient.invalidateQueries({ queryKey: chatSessionKeys.lists() })
    },
  })
}

// Re-export types
export type { ChatSession, ChatMessage }
